// API Tracking Utility
class APITracker {
  constructor() {
    this.calls = [];
    this.isEnabled = true;
  }

  // Track API call start
  trackStart(type, url, method, data = null) {
    if (!this.isEnabled) return null;
    
    const callId = Date.now() + Math.random();
    const call = {
      id: callId,
      type,
      url,
      method,
      data,
      startTime: Date.now(),
      status: 'pending'
    };
    
    this.calls.push(call);
    return callId;
  }

  // Track API call completion
  trackEnd(callId, status, responseData = null, error = null) {
    if (!this.isEnabled) return;
    
    const call = this.calls.find(c => c.id === callId);
    if (!call) return;
    
    const duration = Date.now() - call.startTime;
    
    // Dispatch event for debug panel
    window.dispatchEvent(new CustomEvent('api-call', {
      detail: {
        type: call.type,
        url: call.url,
        method: call.method,
        timestamp: call.startTime,
        duration,
        status,
        error,
        data: responseData
      }
    }));
    
    // Update call status
    call.status = status;
    call.duration = duration;
    call.responseData = responseData;
    call.error = error;
  }

  // Track API call error
  trackError(callId, error) {
    if (!this.isEnabled) return;
    
    const call = this.calls.find(c => c.id === callId);
    if (!call) return;
    
    const duration = Date.now() - call.startTime;
    
    // Dispatch event for debug panel
    window.dispatchEvent(new CustomEvent('api-call', {
      detail: {
        type: call.type,
        url: call.url,
        method: call.method,
        timestamp: call.startTime,
        duration,
        status: 500,
        error: error.message || error,
        data: null
      }
    }));
    
    // Update call status
    call.status = 500;
    call.duration = duration;
    call.error = error;
  }

  // Enable/disable tracking
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  // Get all calls
  getCalls() {
    return this.calls;
  }

  // Clear all calls
  clearCalls() {
    this.calls = [];
  }

  // Get call statistics
  getStats() {
    const total = this.calls.length;
    const successful = this.calls.filter(c => c.status >= 200 && c.status < 300).length;
    const failed = this.calls.filter(c => c.status >= 400).length;
    const avgDuration = this.calls.length > 0 
      ? this.calls.reduce((sum, c) => sum + (c.duration || 0), 0) / this.calls.length 
      : 0;
    
    return {
      total,
      successful,
      failed,
      avgDuration,
      successRate: total > 0 ? (successful / total) * 100 : 0
    };
  }
}

// Create singleton instance
const apiTracker = new APITracker();

// Enhanced fetch wrapper
export const trackedFetch = async (url, options = {}, type = 'unknown') => {
  const method = options.method || 'GET';
  const callId = apiTracker.trackStart(type, url, method, options.body);
  
  try {
    const response = await fetch(url, options);
    
    // Clone the response to avoid consuming the body
    const responseClone = response.clone();
    const responseData = await responseClone.json().catch(() => null);
    
    apiTracker.trackEnd(callId, response.status, responseData);
    
    return response;
  } catch (error) {
    apiTracker.trackError(callId, error);
    throw error;
  }
};

// Track specific API calls
export const trackChatAPI = async (url, options = {}) => {
  return trackedFetch(url, options, 'chat');
};

export const trackTTSAPI = async (url, options = {}) => {
  return trackedFetch(url, options, 'tts');
};

export const trackAuthAPI = async (url, options = {}) => {
  return trackedFetch(url, options, 'auth');
};

export const trackHistoryAPI = async (url, options = {}) => {
  return trackedFetch(url, options, 'history');
};

export const trackTranslationAPI = async (url, options = {}) => {
  return trackedFetch(url, options, 'translation');
};

export default apiTracker;
